import { useState } from 'react'
import { authErrorMessage, refreshUser, resendVerification, signOut } from '../firebase'
import { useStore } from '../store'

/**
 * Shown when an account exists but the address is unconfirmed.
 *
 * The security rules require email_verified, so an unverified account cannot
 * read or write anything — better to say so plainly than to drop the user into
 * an app where every action silently fails.
 */
export function VerifyEmail() {
  const user = useStore((s) => s.user)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stillWaiting, setStillWaiting] = useState(false)

  const resend = async () => {
    setBusy(true); setError(null)
    try { await resendVerification(); setSent(true) }
    catch (e) { setError(authErrorMessage(e)) } finally { setBusy(false) }
  }

  const recheck = async () => {
    setBusy(true); setError(null); setStillWaiting(false)
    try {
      const ok = await refreshUser()
      if (ok) useStore.getState().setUser({ ...user } as never)   // re-render with the fresh claim
      else setStillWaiting(true)
    } catch (e) { setError(authErrorMessage(e)) } finally { setBusy(false) }
  }

  return (
    <div className="signin">
      <div className="signin-card">
        <img src="./favicon.png" alt="" width="52" height="52" style={{ borderRadius: 12 }} />
        <h1>Confirm your email</h1>
        <p>
          We sent a link to <strong>{user?.email}</strong>. Open it, then come back
          and continue.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="google-btn" onClick={() => void recheck()} disabled={busy}>
            {busy ? 'Checking…' : 'I’ve confirmed it'}
          </button>
          <button className="google-btn" onClick={() => void resend()} disabled={busy}>
            Resend email
          </button>
        </div>
        {sent && <p className="signin-note">Sent. It can take a minute to arrive.</p>}
        {stillWaiting && <p className="signin-note">Not confirmed yet — open the link, then try again.</p>}
        {error && <p className="signin-note" style={{ color: 'var(--danger)' }}>{error}</p>}
        <p className="signin-note">
          Wrong address? <button className="linkish" onClick={() => void signOut()}>Sign out</button>
        </p>
      </div>
    </div>
  )
}
